import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { t, useLanguage } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VideoBox, emptyTutorials } from "@/components/HowToVideos";

export default function HowToAdmin({ embedded = false }) {
  useLanguage();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState({});
  const [messages, setMessages] = useState({});
  const [error, setError] = useState("");
  useEffect(() => {
    if (user?.role === "admin") api.get("/how-to").then(({ data }) => setItems(emptyTutorials().map(item => data.find(value => value.slot === item.slot) || item)))
      .catch(() => setError("Could not load videos. Please reload."));
  }, [user?.role]);
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  const changeTitle = (slot, title) => setItems(current => current.map(item => item.slot === slot ? { ...item, title } : item));
  const update = async (item, action) => {
    setBusy(current => ({ ...current, [item.slot]: true }));
    setMessages(current => ({ ...current, [item.slot]: null }));
    try {
      let response;
      if (action === "upload") {
        const file = files[item.slot];
        if (!file || file.size > 100 * 1024 * 1024) throw new Error("Select an MP4 or WebM video up to 100 MB.");
        const data = new FormData(); data.append("file", file); data.append("title", item.title);
        response = await api.post(`/how-to/${item.slot}/video`, data);
      } else if (action === "remove") response = await api.delete(`/how-to/${item.slot}/video`);
      else response = await api.put(`/how-to/${item.slot}/title`, { title: item.title });
      setItems(current => current.map(value => value.slot === item.slot ? response.data : value));
      if (action === "upload") { setFiles(current => ({ ...current, [item.slot]: null })); document.getElementById(`video-${item.slot}`).value = ""; }
      setMessages(current => ({ ...current, [item.slot]: { text: "Changes saved.", error: false } }));
    } catch (e) {
      setMessages(current => ({ ...current, [item.slot]: { text: e.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : e.message || "Could not save changes.", error: true } }));
    } finally { setBusy(current => ({ ...current, [item.slot]: false })); }
  };
  const Container = embedded ? "section" : "main";
  const Heading = embedded ? "h2" : "h1";
  return <Container className={embedded ? "" : "min-h-screen bg-[#FAF7F2] p-5"}><div className="max-w-6xl mx-auto space-y-6">
    {!embedded && <Link to="/app" className="text-amber-800 underline">{t("Back to tree")}</Link>}
    <Heading className="font-serif text-3xl">{t("Manage how-to videos")}</Heading>
    <p>{t("These three videos are visible to all visitors. Upload MP4 or WebM files up to 100 MB. Removing a video keeps its title and shows Coming soon.")}</p>
    <Link to="/#how-to" className="text-amber-800 underline inline-block">{t("View landing page")}</Link>
    {error && <p role="alert">{t(error)}</p>}
    {!items && !error && <p role="status">{t("Loading...")}</p>}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{items?.map(item => <section key={item.slot} className="bg-white border rounded-xl p-4 space-y-4">
      <h2 className="font-serif text-xl">{t("Tutorial")} {item.slot}</h2>
      <label className="block" htmlFor={`title-${item.slot}`}>{t("Video title")}</label>
      <Input id={`title-${item.slot}`} maxLength={120} value={item.title} disabled={busy[item.slot]} onChange={event => changeTitle(item.slot, event.target.value)} />
      <Button disabled={busy[item.slot]} onClick={() => update(item, "title")}>{t("Save title")}</Button>
      <VideoBox item={item} />
      <label className="block" htmlFor={`video-${item.slot}`}>{t("Select video")}</label>
      <input className="block w-full text-sm" id={`video-${item.slot}`} type="file" accept=".mp4,.webm,video/mp4,video/webm" disabled={busy[item.slot]} onChange={event => setFiles(current => ({ ...current, [item.slot]: event.target.files[0] }))} />
      <div className="flex flex-wrap gap-2"><Button disabled={busy[item.slot] || !files[item.slot]} onClick={() => update(item, "upload")}>{busy[item.slot] ? t("Saving...") : t(item.video_url ? "Replace video" : "Upload video")}</Button>
        {item.video_url && <Button variant="outline" disabled={busy[item.slot]} onClick={() => update(item, "remove")}>{t("Remove video")}</Button>}</div>
      {messages[item.slot] && <p role={messages[item.slot].error ? "alert" : "status"}>{t(messages[item.slot].text)}</p>}
    </section>)}</div>
  </div></Container>;
}
